import { DEFAULT_FILE_MESSAGE } from '../constants';
import type { Label } from '../types';

/**
 * Formats an array of file type labels into a human-readable string
 * @param types - Array of file type labels
 * @param maxItems - Maximum number of items to display before truncation
 * @returns Formatted string (e.g., 'JPEG, PNG, or PDF', 'PDF +2 more')
 */
export const formatFileTypes = (
  rawTypes: Label[],
  maxItems: number = 5,
): string => {
  const types = rawTypes.filter((type) => type && type.trim() !== '');

  if (types.length === 0) {
    return 'files';
  }

  // Handle truncation for long lists
  if (types.length > maxItems) {
    const firstItems = types.slice(0, maxItems).join(', ');
    const remainingCount = types.length - maxItems;
    return `${firstItems} +${remainingCount} more`;
  }

  // Handle specific cases for proper grammar
  if (types.length === 1) {
    return types[0] || '';
  }
  if (types.length === 2) {
    return `${types[0] || ''} or ${types[1] || ''}`;
  }

  // Handle 3+ items: "A, B, or C"
  const allButLast = types.slice(0, -1).join(', ');
  const lastItem = types[types.length - 1] || '';
  return `${allButLast}, or ${lastItem}`;
};

/**
 * Generates a complete file upload message with proper formatting
 * @param fileTypes - Array of file type labels
 * @param template - Message template with {fileTypes} placeholder
 * @param maxItems - Maximum items before truncation
 * @returns Complete formatted message
 */
export const generateFileMessage = (
  fileTypes: Label[],
  template: string = 'Drag and drop your {fileTypes} files here, or browse for files',
  maxItems: number = 5,
): string => {
  if (!fileTypes || fileTypes.length === 0) {
    return DEFAULT_FILE_MESSAGE;
  }

  const formattedTypes = formatFileTypes(fileTypes, maxItems);
  return template.replace('{fileTypes}', formattedTypes);
};
